import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ShoppingCart, Store, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const { t } = useLanguage();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        toast.error("Could not load product");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  if (loading) return <div className="h-screen flex items-center justify-center">Loading...</div>;
  if (!product) return <div className="h-screen flex items-center justify-center">Product not found</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/marketplace" className="inline-flex items-center text-stone-500 hover:text-primary mb-8">
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Marketplace
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* Image */}
        <div className="aspect-square rounded-3xl overflow-hidden bg-stone-100 shadow-inner">
          <img 
            src={product.image_url} 
            alt={product.title} 
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
          />
        </div>

        {/* Details */}
        <div className="space-y-8">
          <div>
            <div className="flex items-center justify-between mb-2">
               <span className="text-sm font-medium text-primary uppercase tracking-wider">{product.category}</span>
               <div className="flex items-center text-stone-400 text-sm gap-2">
                 <span>{product.views} views</span>
               </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">{product.title}</h1>
            <Link to={`/store/${product.seller_id}`} className="inline-flex items-center text-stone-600 hover:text-primary transition-colors">
              <Store className="mr-2 h-5 w-5" />
              <span className="font-medium underline decoration-stone-300 hover:decoration-primary">{product.seller_name}</span>
            </Link>
          </div>

          <div className="text-3xl font-bold text-primary">
            {product.price} DZD
            <span className="block text-sm font-normal text-stone-500 mt-1">
              Delivery: {product.delivery_option === 'included' ? 'Included in price' : product.delivery_option === 'free' ? 'Free' : `+ ${product.delivery_price} DZD`}
            </span>
          </div>

          <p className="text-lg text-stone-600 leading-relaxed">
            {product.description}
          </p>

          <div className="pt-6 border-t border-stone-200">
            <Button onClick={() => addToCart(product)} size="lg" className="w-full md:w-auto btn-primary text-lg px-12">
              <ShoppingCart className="mr-2 h-5 w-5" /> {t('addToCart')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
